import {
  get,
  isEmpty,
  isNaN,
  isNull,
  isNumber,
  isString,
  isUndefined,
  transform,
  set,
} from 'lodash'
import {
  ELECTRONIC_GIFT_CARD_MAX,
  ELECTRONIC_GIFT_CARD_MIN,
} from 'shared/constants'
import splitName from 'lib/split-name'
import emojiRegex from 'emoji-regex'

const isBlank = (value) => (isEmpty(value) && !isNumber(value)) || isNaN(value)

const isNumeric = (value) => Number(parseFloat(value)) == value

// Helper to wrap one or more rules into a single function, which returns the first error
const join = (rules) => (value, data) =>
  rules
    .map((rule) => {
      if (typeof rule === 'function') {
        return rule(value, data)
      }
      return rule.rule(value, data, rule.msg)
    })
    .filter((error) => !!error)[0 /* first error */]

export const EMOJI_REGEX = emojiRegex()
export const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/i
export const PO_BOX_REGEX = /((p\.?\s*o\.?)|(post\s*(office)?))\s*box/i

// https://gist.github.com/dperini/729294
// https://mathiasbynens.be/demo/url-regex
// NOTE: Edited to not require protocol!!!
export const URL_REGEX =
  /^(?:(?:https?|ftp):\/\/)?(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i

export const MSG_EMAIL_REQUIRED = 'Oops...email is required'

export function between(min, max, prefix = '') {
  return (value) => {
    if (isPresent(value) && (value < min || value > max)) {
      return `Must be between ${prefix}${min} and ${prefix}${max}`
    }
  }
}

export const dateString = (value) => {
  if (isPresent(value) && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return 'Invalid date'
  }
}

export const currency = (value) => {
  // optional $, commas, and cents
  if (isPresent(value) && !/^\$?\d{1,3}(,?\d{3})*(\.\d{1,2})?$/.test(value)) {
    return 'Please enter a valid dollar amount'
  }
}

export const electronicGiftCardAmount = (value) =>
  required(value) ||
  numeric(value) ||
  between(ELECTRONIC_GIFT_CARD_MIN, ELECTRONIC_GIFT_CARD_MAX, '$')(value)

export function email(value) {
  // Has only one @, at least one character before the @, before the period and after it
  if (isPresent(value) && !EMAIL_REGEX.test(value)) {
    return 'Invalid email address'
  }
}

export const fullName = (name) => {
  if (!splitName(name).lastName) {
    return 'Please add your last name'
  }
}

export const isPresent = (value) =>
  !(
    isUndefined(value) ||
    isNull(value) ||
    isNaN(value) ||
    (isString(value) && isEmpty(value))
  )

export const confirmEmail = (value, email) => {
  if (isPresent(value) && value !== email) {
    return 'Email address does not match'
  }
}

export function notEmail(value) {
  // Has only one @, at least one character before the @, before the period and after it
  if (EMAIL_REGEX.test(value)) {
    return "Can't be an email address"
  }
}

export const notPoBox = (value) => {
  if (PO_BOX_REGEX.test(value)) {
    return "Can't be a P.O. Box address"
  }
  return false
}

export function integer(value) {
  if (!Number.isInteger(Number(value))) {
    return 'Must be an integer'
  }
}

export function match(field) {
  return (value, data) => {
    if (data) {
      if (value !== data[field]) {
        return 'Do not match'
      }
    }
  }
}

export function maxLength(max) {
  return (value) => {
    if (isPresent(value) && value.length > max) {
      return `Must be no more than ${max} characters`
    }
  }
}

export function maxValue(max, prefix = '') {
  return (value) => {
    if (isPresent(value) && value > max) {
      return `Must not exceed ${prefix}${max}`
    }
  }
}

export function minLength(min) {
  return (value) => {
    if (isPresent(value) && value.length < min) {
      return `Must be at least ${min} characters`
    }
  }
}

export function minValue(min, prefix = '') {
  return (value) => {
    if (isPresent(value) && value < min) {
      return `Must be at least ${prefix}${min}`
    }
  }
}

export function numeric(value) {
  if (isPresent(value) && !isNumeric(value)) {
    return 'Must be a number'
  }
}

export function present(value) {
  if (!isPresent(value)) {
    return 'Must not be left blank'
  }
}

export function notEquals(string1, string2) {
  if (string1 && string2 && string1.toLowerCase() === string2.toLowerCase()) {
    return 'Must not be identical'
  }
}

export function oneOf(enumeration) {
  return (value) => {
    if (enumeration.indexOf(value) !== -1) {
      return `Must be one of: ${enumeration.join(', ')}`
    }
  }
}

export function required(value, data, msg = 'Required') {
  if (isBlank(value)) {
    return msg
  }
}

export function url(value) {
  // NOTE: Does not require protocol!
  if (!isPresent(value) || !URL_REGEX.test(value)) {
    return 'Invalid URL'
  }
}

// https://gist.github.com/symm/7145799
export const bankAccountNumber = (str) => {
  if (!/^\d{1,17}$/.test(str)) return 'Invalid bank account number'
}

// https://gist.github.com/symm/7145799
// ¯\_(ツ)_/¯
export const usRoutingNumber = (str) => {
  let t
  let n
  let r
  let i
  let s
  let o
  let u
  if (!/^\d{9}$/.test(str)) return 'Routing number must have 9 digits'
  s = 0
  // eslint-disable-next-line no-multi-assign
  for (t = o = 0, u = str.length - 1; o <= u; t = o += 3) {
    n = parseInt(str.charAt(t)) * 3
    r = parseInt(str.charAt(t + 1)) * 7
    i = parseInt(str.charAt(t + 2))
    s += n + r + i
  }
  if (s === 0 || s % 10 !== 0) return 'Invalid routing number'
}

export const usZip = (value) => {
  const fiveDigitZip = value.match(/^(\d{5})-?(\d{4})?$/)
  if (!fiveDigitZip) {
    return 'Must be a valid US zip'
  }
}

export const notContainEmoji = (value) => {
  if (isPresent(value) && EMOJI_REGEX.test(value)) {
    return "Can't contain any emoji"
  }
}

export function createValidator(rules) {
  return (data = {}) => {
    let errors = {}
    Object.keys(rules).forEach((key) => {
      const rule = join([].concat(rules[key])) // concat enables both functions and arrays of functions
      const error = rule(get(data, key), data)
      if (error) {
        errors[key] = error
      }
    })
    /*
      errors can potentially have nested obj keys in dot notation (e.g. {'foo.bar.baz': 'required'})
      transform these into POJOs
      {
        foo: {
          bar: {
            baz: 'required'
          }
        }
      }
    */
    errors = transform(errors, (obj, val, key) => set(obj, key, val))
    return errors
  }
}
